import { Trans, useTranslation } from "react-i18next";
import type { Decision } from "../../../api/access/decisions";
import { useProjects } from "../../../projects/projectContext";
import BlockedState from "../../../ui/feedback/BlockedState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { Link } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import type { NewThreadStatus } from "../prepare";

/**
 * Why no Thread can start here, where New Thread would be (P-B, F107): the read-only DMW core
 * points to the projects, where work starts; a missing permission shows what is missing and who
 * can add it, with Ask (BlockedState, GS-8); a project that failed to load says so, with a
 * retry. The core's sentence is one key, its words provisional until Johannes answers
 * IM-THREADS' open point.
 */
export default function NewThreadBlocked({ status, decision }: { status: Extract<NewThreadStatus, "failed" | "coreReadOnly" | "notPermitted">; decision: Decision }) {
  const { t } = useTranslation();
  const projects = useProjects();
  if (status === "failed") {
    const chosen = projects.selectedProject;
    return chosen === null ? null : <LoadStatus state={chosen} loadingLabel={t("common.loading")} onRetry={projects.refreshSelectedProject} compact />;
  }
  if (status === "notPermitted") return <BlockedState decision={decision} />;
  return (
    <p className="app-text-muted text-xs">
      <Trans i18nKey="threads.start.coreReadOnly" components={{ projects: <Link to={appPaths.projects()} className="app-link underline" /> }} />
    </p>
  );
}

/**
 * The core's reason as plain words, for New Thread's `unavailable` (F107): one key, the projects
 * named without a link, since a tooltip can't hold one; the projects menu is the way there.
 */
export function CoreReadOnlyReason() {
  return <Trans i18nKey="threads.start.coreReadOnly" components={{ projects: <span /> }} />;
}
